/**
 * SoloLayout - Layout wrapper for solo provider portal routes
 *
 * This is the new architecture layout for independent providers.
 * It replaces the complex ProviderLayout guard chain with a simpler
 * single-check approach using the bootstrap API.
 *
 * Key features:
 * - Uses useSoloStatus hook to validate provider state
 * - Handles subscription and setup validation in one place
 * - Provides SoloContext to child routes
 * - No chicken-and-egg problems with guards
 */

import type { FC } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/app/providers/AuthProvider';
import { AccessBoundary } from '@/app/routing/access/AccessBoundary';
import type { AccessContext } from '@/app/routing/access/types';
import { useSoloStatus } from '@/features/auth/hooks/useSoloStatus';
import type { SubscriptionStatus } from '@/shared/api/bootstrap';
import { LoadingScreen } from '@/shared/ui/loading';
import { logger } from '@/shared/utils/logger';
import { paths } from '../../builders/paths';

/**
 * Context value for solo provider portal
 */
export interface SoloContextValue {
  providerId: string;
  providerName: string;
  providerEmail: string;
  providerPictureUrl: string | null;
  setupComplete: boolean;
  subscriptionStatus: SubscriptionStatus;
  refetch: () => Promise<unknown>;
}

const SoloContext = createContext<SoloContextValue | null>(null);

/**
 * Hook to access solo provider context
 * Must be used within SoloLayout
 */
export const useSoloContext = (): SoloContextValue => {
  const context = useContext(SoloContext);
  if (!context) {
    throw new Error('useSoloContext must be used within SoloLayout');
  }
  return context;
};

/**
 * Check if subscription status is active
 */
const isActiveSubscription = (status: SubscriptionStatus): boolean => {
  return ['active', 'trialing'].includes(status);
};

/**
 * SoloLayout Component
 */
export const SoloLayout: FC = () => {
  const location = useLocation();
  const { isLoading: authLoading, isAuthenticated, accessToken } = useAuthContext();
  const { data: soloStatus, isLoading: statusLoading, error, refetch } = useSoloStatus({
    enabled: isAuthenticated && !authLoading && !!accessToken,
    accessToken,
  });

  // Build context value
  const contextValue = useMemo<SoloContextValue | null>(() => {
    if (!soloStatus?.providerProfile.exists) return null;

    return {
      providerId: soloStatus.providerProfile.id || '',
      providerName: soloStatus.providerProfile.name,
      providerEmail: soloStatus.providerProfile.email,
      providerPictureUrl: soloStatus.providerProfile.pictureUrl,
      setupComplete: soloStatus.providerProfile.setupComplete,
      subscriptionStatus: soloStatus.providerProfile.soloSubscriptionStatus,
      refetch,
    };
  }, [soloStatus, refetch]);

  // Build access context for AccessBoundary (must be before early returns per rules-of-hooks)
  const accessContext = useMemo<AccessContext>(() => ({
    isAuthenticated: true,
    role: 'independent_provider',
    workspace: 'solo',
    subscriptionStatus: soloStatus?.providerProfile.soloSubscriptionStatus || 'none',
    setupComplete: soloStatus?.providerProfile.setupComplete || false,
  }), [soloStatus?.providerProfile.soloSubscriptionStatus, soloStatus?.providerProfile.setupComplete]);

  // Loading state - show loading screen while auth or status is loading
  if (authLoading || statusLoading) {
    return <LoadingScreen message="Loading your workspace..." />;
  }

  // Not authenticated - redirect to home
  // IMPORTANT: Only redirect after auth0 has finished loading to avoid false negatives
  if (!isAuthenticated) {
    logger.warn('SoloLayout: Not authenticated, redirecting to home');
    return <Navigate to={paths.home()} replace />;
  }

  // API error - show loading/retry instead of immediate redirect
  // This prevents logout glitch during navigation when API temporarily fails
  if (error) {
    logger.error('SoloLayout: Error fetching solo status', error);
    // If we have cached data, continue with it (React Query's staleWhileRevalidate)
    if (soloStatus?.providerProfile.exists) {
      logger.info('SoloLayout: Using cached data despite error');
      // Continue to render with cached data below
    } else {
      // No cached data - show loading with retry option instead of redirect
      // This is more user-friendly than sudden logout
      return <LoadingScreen message="Reconnecting..." />;
    }
  }

  // No provider profile - redirect to onboarding
  if (!soloStatus?.providerProfile.exists) {
    logger.info('SoloLayout: No provider profile, redirecting to onboarding');
    return <Navigate to={paths.onboarding.roleSelect()} replace />;
  }

  const { soloSubscriptionStatus: subscriptionStatus, setupComplete } = soloStatus.providerProfile;

  // No active subscription - redirect to subscription page
  if (!isActiveSubscription(subscriptionStatus)) {
    logger.info('SoloLayout: No active subscription, redirecting to plans', {
      currentStatus: subscriptionStatus,
    });
    return <Navigate to={paths.providerLanding()} replace />;
  }

  // Setup incomplete - redirect to setup (unless already on setup route)
  if (!setupComplete && !location.pathname.includes('/setup')) {
    logger.info('SoloLayout: Setup incomplete, redirecting to setup');
    return <Navigate to={paths.provider.independentSetup()} replace />;
  }

  // All checks passed - render children with context
  return (
    <SoloContext.Provider value={contextValue}>
      <AccessBoundary context={accessContext}>
        <Outlet />
      </AccessBoundary>
    </SoloContext.Provider>
  );
};
